import cx from "classnames";
import React from "react";

import SellerProPlusBadge from "@/mk2/apps/bazaar/components/SellerProPlusBadge";
import { SellerEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarSellerReviewsFromBuyersUrl, bazaarSellerUrl } from "@/mk2/apps/bazaar/urls";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Dot } from "@/mk2/components/Dot";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { TrimContent } from "@/mk2/components/TrimContent";
import { url } from "@/mk2/helpers/urls";
import { interpolate } from "@/mk2/services/i18n";
import {
    BZ2_HIDE_TERMS,
    BZ2_SHOW_COMPLETE_TERMS,
    BZ2_TERMS,
    counter_ENTRIES,
    counter_RATINGS,
} from "@/mk/autogenerated/translations/SellerInfo.9d4415e2c27598c113a7be9f02e5c94f";
import { linebreaksbr } from "@/mk/bazaar/common/filters";

import styles from "./SellerInfo.mscss";

interface OwnProps {
    className?: string;
    seller: SellerEntity;
    hideSeller?: boolean;
    height?: number;
}

type Props = OwnProps;

let SellerInfo: React.FunctionComponent<OwnProps> = ({
    className,
    seller,
    hideSeller,
    height = 150,
}) => {
    const bazaarUrl = url(bazaarSellerUrl, { username: seller.user.username });

    return (
        <Panel className={cx(styles.SellerInfo, className)}>
            {hideSeller ? (
                <div className={styles.SellerInfo__header__terms}>{BZ2_TERMS}:</div>
            ) : (
                <div className={styles.SellerInfo__header}>
                    <AvatarImg
                        size={50}
                        user={seller.user}
                        href={bazaarUrl}
                        className={styles.SellerInfo__header__avatar}
                    />
                    <div className={styles.SellerInfo__header__info}>
                        <div className={styles.SellerInfo__header__info__username}>
                            <Link
                                to={bazaarUrl}
                                className={styles.SellerInfo__header__info__username__link}
                            >
                                {seller.user.username}
                            </Link>
                            {seller.location && (
                                <>
                                    <Dot />
                                    <span className={styles.SellerInfo__header__info__location}>
                                        {seller.location}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className={styles.SellerInfo__header__info__reviews}>
                            <SellerProPlusBadge user={seller.user} isSeller={false} />
                            <Dot spaces={1} />
                            <Link
                                className={styles.SellerInfo__header__info__reviews__productsCount}
                                to={bazaarUrl}
                            >
                                {interpolate(counter_ENTRIES, {
                                    counter: seller.productsCount,
                                    count: seller.productsCount,
                                })}
                            </Link>
                            <Dot spaces={1} />
                            <Link
                                className={styles.SellerInfo__header__info__reviews__reviewsCount}
                                to={url(bazaarSellerReviewsFromBuyersUrl, {
                                    username: seller.username,
                                })}
                            >
                                {interpolate(counter_RATINGS, {
                                    counter: seller.reviewsCount,
                                    count: seller.reviewsCount,
                                })}
                            </Link>
                        </div>
                        <div className={styles.SellerInfo__mask} />
                    </div>
                </div>
            )}
            {seller.terms && seller.terms.trim() && (
                <div
                    className={cx(
                        styles.SellerInfo__terms,
                        !hideSeller && styles.SellerInfo__borderTop,
                    )}
                >
                    <TrimContent
                        collapseLabel={BZ2_HIDE_TERMS}
                        expandLabel={BZ2_SHOW_COMPLETE_TERMS}
                        height={height}
                    >
                        {linebreaksbr(seller.terms.trim())}
                    </TrimContent>
                </div>
            )}
        </Panel>
    );
};

SellerInfo.displayName = "SellerInfo";

SellerInfo = React.memo<Props>(SellerInfo);

export { SellerInfo };
